/**
 * PageAccessEditor — grant access by ticking PAGES.
 *
 * An admin's actual question is "what should this person be able to open?", not
 * "which of these nine stored role strings composes to that?". So this editor
 * shows the app's pages, grouped by module, and translates the ticked set into
 * roles on save (page-registry `rolesForPages`). Roles remain the stored
 * primitive — the server validates every string against the spec — they just
 * stop being the thing a human has to reason about.
 *
 * THE HONESTY REQUIREMENT. Roles are coarser than pages: there is no role that
 * grants CRM Follow-ups alone, so ticking it necessarily also opens Customers,
 * Home, and the rest of the CSM read set. An editor that quietly applied that
 * would be granting access the admin didn't ask for. Instead:
 *   - pages the solver adds are shown as "also opens" BEFORE saving,
 *   - and they appear pre-ticked-but-marked so the checkbox state never lies
 *     about what the person will actually be able to reach.
 *
 * Full role control still exists in the Advanced expander (RoleSetEditor) for the
 * things pages can't express — Expense approver, CRM write/delete capabilities.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@andavo/web-ui';
import { useSetUserRoles } from '../data-access/useAdmin';
import {
  MODULE_LABEL,
  PAGE_MODULES,
  pageByKey,
  pageKeysForRoles,
  pagesForModule,
  rolesForPages,
  rolesToPersist,
  type PageModule,
} from '../lib/page-registry';
import { storedRolesFromAdminUser } from '../lib/rbac-model';
import type { AdminUser } from '../types';
import { AdminError, Panel } from './primitives';

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

function labelFor(pageKey: string): string {
  const page = pageByKey(pageKey);
  if (!page) return pageKey;
  return page.module === 'admin'
    ? page.label
    : `${MODULE_LABEL[page.module]} › ${page.label}`;
}

export function PageAccessEditor({
  user,
  onDone,
}: {
  user: AdminUser;
  /** Called after a successful save so the parent can collapse the editor. */
  onDone?: () => void;
}) {
  const baselineRoles = useMemo(() => storedRolesFromAdminUser(user), [user]);
  const baselinePages = useMemo(
    () => pageKeysForRoles(baselineRoles),
    [baselineRoles],
  );

  const [selected, setSelected] = useState<string[]>(baselinePages);
  const setRoles = useSetUserRoles();

  // Re-seed when a different user is opened, or when a refetch changes the
  // authoritative set. Keyed on id + baseline contents, same contract as the
  // role editor this replaced.
  // biome-ignore lint/correctness/useExhaustiveDependencies: id+baseline identity is the intended reseed trigger
  useEffect(() => {
    setSelected(baselinePages);
  }, [user.id, baselinePages]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // What ticking these pages actually resolves to. `granted` is the truth about
  // what the person will be able to open; `extra` is the over-grant to disclose.
  const solution = useMemo(() => rolesForPages(selected), [selected]);
  const grantedSet = useMemo(
    () => new Set(solution.granted),
    [solution.granted],
  );

  // What a save will actually persist: the solver's roles plus any held role
  // that opens no page (commissions:reviewer) — pages cannot express those, so
  // a page edit must not revoke them. See page-registry `rolesToPersist`.
  const rolesToSave = useMemo(
    () => rolesToPersist(solution.roles, baselineRoles),
    [solution.roles, baselineRoles],
  );

  const dirty = !sameSet(rolesToSave, baselineRoles);

  const togglePage = (key: string) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleModule = (module: PageModule) => {
    const keys = pagesForModule(module).map((p) => p.key);
    const allOn = keys.every((k) => selectedSet.has(k));
    setSelected((prev) =>
      allOn
        ? prev.filter((k) => !keys.includes(k))
        : [...new Set([...prev, ...keys])],
    );
  };

  const handleSave = () => {
    // `baseline_roles` is the set this editor started from. The server applies
    // the save as a DIFF against what is stored NOW (add = roles − baseline,
    // remove = baseline − roles), so a role another admin granted or revoked
    // after this editor loaded — commissions:reviewer, carried forward from the
    // cached baseline by rolesToPersist — is left as they set it instead of
    // being reverted by an unrelated page edit (Codex P2, 2026-10-18).
    setRoles.mutate(
      { user_id: user.id, roles: rolesToSave, baseline_roles: baselineRoles },
      {
        onSuccess: () => {
          toast.success(
            rolesToSave.length === 0
              ? `Removed all access for ${user.email}.`
              : `${user.email} can now open ${solution.granted.length} page${
                  solution.granted.length === 1 ? '' : 's'
                }.`,
          );
          onDone?.();
        },
        onError: (err) =>
          toast.error((err as Error)?.message ?? 'Failed to save access'),
      },
    );
  };

  return (
    <Panel
      title={`Access — ${user.email}`}
      actions={
        <div className="flex items-center gap-2">
          <Button
            data-testid="page-access-reset"
            disabled={!dirty || setRoles.isPending}
            onClick={() => setSelected(baselinePages)}
            size="sm"
            type="button"
            variant="secondary"
          >
            Reset
          </Button>
          <Button
            data-testid="page-access-save"
            disabled={!dirty || setRoles.isPending}
            onClick={handleSave}
            size="sm"
            type="button"
          >
            {setRoles.isPending ? 'Saving…' : 'Save access'}
          </Button>
        </div>
      }
    >
      <div data-testid="page-access-editor" data-user-id={user.id}>
        {setRoles.isError ? (
          <div className="mb-3">
            <AdminError
              message={`Save failed: ${
                (setRoles.error as Error)?.message ?? 'unknown'
              }`}
            />
          </div>
        ) : null}

        <p className="mb-3 font-inter text-content-secondary text-sm">
          Tick the pages this person should be able to open. No pages ticked
          means no access anywhere.
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          {PAGE_MODULES.map((module) => {
            const pages = pagesForModule(module);
            const allOn = pages.every((p) => selectedSet.has(p.key));
            return (
              <fieldset
                className="rounded-md border border-border-subtle p-3"
                data-testid={`page-module-${module}`}
                key={module}
              >
                <legend className="px-1">
                  <button
                    className="font-sans font-medium text-content-primary text-sm hover:text-content-action"
                    data-testid={`page-module-toggle-${module}`}
                    onClick={() => toggleModule(module)}
                    type="button"
                  >
                    {MODULE_LABEL[module]}
                    <span className="ml-1.5 font-inter font-normal text-content-tertiary text-xs">
                      {allOn ? 'none' : 'all'}
                    </span>
                  </button>
                </legend>
                <div className="flex flex-col gap-1.5">
                  {pages.map((page) => {
                    const ticked = selectedSet.has(page.key);
                    // Granted-but-not-ticked: the solver had to include it. Show
                    // it as checked (that IS what will happen) and say why.
                    const impliedOnly = !ticked && grantedSet.has(page.key);
                    return (
                      <label
                        className="flex cursor-pointer items-start gap-2 font-inter text-content-secondary text-sm"
                        key={page.key}
                      >
                        <input
                          checked={ticked || impliedOnly}
                          className="mt-0.5 size-4 shrink-0 rounded border-border-subtle accent-content-action"
                          data-implied={impliedOnly ? 'true' : undefined}
                          data-testid={`page-checkbox-${page.key}`}
                          disabled={setRoles.isPending}
                          onChange={() => togglePage(page.key)}
                          type="checkbox"
                        />
                        <span>
                          <span className="text-content-primary">
                            {page.label}
                          </span>
                          {impliedOnly ? (
                            <span className="ml-1.5 rounded-full bg-amber-500/10 px-1.5 py-0.5 font-medium text-amber-700 text-xs">
                              included by role
                            </span>
                          ) : null}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>

        <SaveSummary roles={rolesToSave} solution={solution} />
      </div>
    </Panel>
  );
}

/**
 * What will actually be written, in both vocabularies: the roles that get stored
 * and — when the two differ — the pages that come along with them. `roles` is
 * the persisted set (solver + preserved page-less roles), not the solver's alone,
 * so the summary never claims "no roles" while a save keeps one.
 */
function SaveSummary({
  roles,
  solution,
}: {
  roles: readonly string[];
  solution: ReturnType<typeof rolesForPages>;
}) {
  return (
    <div
      className="mt-4 rounded-md border border-border-subtle bg-surface-background p-3"
      data-testid="page-access-summary"
    >
      <h3 className="mb-1 font-sans font-medium text-content-primary text-sm">
        Saves as{' '}
        <span className="font-inter font-normal text-content-tertiary text-xs">
          (roles are how access is stored)
        </span>
      </h3>
      {roles.length === 0 ? (
        <p
          className="font-inter text-content-tertiary text-xs"
          data-testid="page-access-summary-empty"
        >
          No roles — this person will have no access on any surface
          (default-deny).
        </p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {roles.map((r) => (
            <li
              className="rounded-full bg-surface-action-subtle px-2 py-0.5 font-mono text-content-action text-xs"
              data-role={r}
              key={r}
            >
              {r}
            </li>
          ))}
        </ul>
      )}

      {solution.extra.length > 0 ? (
        <div className="mt-2" data-testid="page-access-overgrant">
          <p className="font-inter font-medium text-amber-700 text-xs">
            Also opens {solution.extra.length} page
            {solution.extra.length === 1 ? '' : 's'} you didn’t tick — the
            closest role includes {solution.extra.length === 1 ? 'it' : 'them'}:
          </p>
          <p className="mt-0.5 font-inter text-content-secondary text-xs">
            {solution.extra.map(labelFor).join(' · ')}
          </p>
        </div>
      ) : null}

      {solution.unreachable.length > 0 ? (
        <div className="mt-2" data-testid="page-access-unreachable">
          <p className="font-inter font-medium text-red-600 text-xs">
            No role grants{' '}
            {solution.unreachable.length === 1 ? 'this' : 'these'} without full
            admin:
          </p>
          <p className="mt-0.5 font-inter text-content-secondary text-xs">
            {solution.unreachable.map(labelFor).join(' · ')}
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default PageAccessEditor;
